import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { PurgeMode } from "./useReadwiseIntegration";

const PURGE_PHRASE = "PURGE";

const PURGE_LABELS: Record<PurgeMode, { title: string; body: string }> = {
  supplementals: {
    title: "Purge all Supplemental highlights",
    body: "Removes every Readwise highlight in this workspace whose source is Supplementals. Re-sync will pull current ones back.",
  },
  tweets: {
    title: "Purge all Tweet highlights",
    body: "Removes every Readwise highlight in this workspace whose source is Tweets. Re-sync will pull current ones back.",
  },
  all: {
    title: "Purge ALL Readwise highlights",
    body: "Removes every Readwise highlight in this workspace, regardless of source. Library items remain. This cannot be undone.",
  },
};

interface Props {
  mode: PurgeMode | null;
  onClose: () => void;
  purging: boolean;
  onConfirm: (mode: PurgeMode) => Promise<void>;
}

export function ReadwisePurgeDialog({ mode, onClose, purging, onConfirm }: Props) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!mode) setText("");
  }, [mode]);

  const handle = async () => {
    if (!mode) return;
    if (text.trim().toUpperCase() !== PURGE_PHRASE) {
      toast.error(`Type ${PURGE_PHRASE} to confirm.`);
      return;
    }
    await onConfirm(mode);
    onClose();
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode ? PURGE_LABELS[mode].title : ""}</DialogTitle>
          <DialogDescription>{mode ? PURGE_LABELS[mode].body : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>
            Type <span className="font-mono font-semibold">{PURGE_PHRASE}</span> to confirm
          </Label>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={purging}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handle}
            disabled={purging || text.trim().toUpperCase() !== PURGE_PHRASE}
          >
            {purging ? "Purging…" : "Purge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
